/**
 * One place that notices the one-off Event Pass gate, whatever screen hit it.
 *
 * Scores are written from a dozen places (the draw grids, the bracket, Swiss, the Games tab, the
 * finals pages, the league screens…) and each grew its own error handling — some toast the
 * message, some swallow it. An organizer who dismissed the offer and then tried to score got an
 * error line and nothing to act on. Rather than teach every call site, the API layer announces
 * the gate here and the app shell opens the offer (PassGateListener), EVERY time.
 *
 * A caller that already routes the error through handlePaywallError marks it handled, so the
 * offer is not opened twice. Dependency-free: imported by api.ts, which everything imports.
 */

export const PASS_GATE_EVENT = 'sp:pass-gate';
const PASS_GATE_CODES = ['event_pass_required', 'event_pass_topup_required', 'player_pro_required'];

const handled = new WeakSet<object>();

export const isPassGatePayload = (x: unknown): x is Record<string, any> =>
    !!x && typeof x === 'object' && PASS_GATE_CODES.includes((x as {code?: string}).code ?? '');

/**
 * Fired whenever the offer is put in front of the organizer because a request was refused. The
 * timed offer on a first landing listens for it: "sixty seconds after entry, or the first score,
 * WHICHEVER COMES FIRST" — so once the score has raised the offer, the timer must not raise it
 * a second time.
 */
export const PASS_OFFER_SHOWN_EVENT = 'sp:pass-offer-shown';

/**
 * The draw setup announces when the format the organizer has chosen is locked by the PLAN (not by
 * the pass). While it is, the pass banner stays out of the way: a pass would not open that
 * format, so offering one next to the plan lock would be two asks for one problem.
 * detail: {uid: string; locked: boolean}
 */
export const FORMAT_PLAN_LOCKED_EVENT = 'sp:format-plan-locked';

function noteOfferShown(): void {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
    window.dispatchEvent(new CustomEvent(PASS_OFFER_SHOWN_EVENT, {detail: null}));
}

/** Called by a surface that opens the offer itself, with its own context (retry, return path). */
export function markPassGateHandled(payload: unknown): void {
    if (payload && typeof payload === 'object') handled.add(payload as object);
    if (isPassGatePayload(payload)) noteOfferShown();
}

/**
 * Called by the API layer for every error body. Deferred one task so the caller's own
 * catch/then (a microtask) gets to claim it first.
 */
export function announcePassGate(payload: unknown): void {
    if (!isPassGatePayload(payload) || typeof window === 'undefined') return;
    setTimeout(() => {
        if (handled.has(payload)) return;
        noteOfferShown();
        window.dispatchEvent(new CustomEvent(PASS_GATE_EVENT, {detail: payload}));
    }, 0);
}
